import React, { useEffect, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { CalendarDays, ChevronLeft, ChevronRight, HeartHandshake, Layers, Lightbulb, ListFilter, MapPin, Sparkles, X, Zap } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { logUsageEvent } from '../../../src/lib/logger';
import { useToast } from '../../../src/contexts/ToastContext';
import { MapaLead } from '../../../types';
import { clusterLeads, familiaTag, ClusterPrioridade } from '../../../src/lib/leadClusters';
import { META_CONCLUIDAS_DIA, META_PLANEJADAS_DIA } from './rotaTypes';
import { isPresencial, isRemota } from '../../../src/lib/modalidade';

const META_DIA = META_PLANEJADAS_DIA; // planejar 15/dia dá margem p/ bater as 12 concluídas (adendo §4)

interface WeekVisita {
    id: number;
    titulo: string;
    vencimento: string;
    modalidade?: string | null;
    tipo?: string | null;
    status: string;
    data_conclusao?: string | null;
    negociacao_id?: number | null;
    negociacoes?: { titulo?: string | null; cidade?: string | null; consumo_medio_kwh?: number | null } | null;
}

interface AgendaSemanaProps {
    leads: MapaLead[];
    vendedores: string[];          // responsáveis efetivos (filtro)
    vendedorId: string;            // responsável default p/ novas visitas
    profileId: string;
    dataSel: string;               // dia selecionado no painel compartilhado
    onSelectDate: (iso: string) => void;
    onWeekChange: (weekStartIso: string) => void; // espelha a semana exibida (Segunda) p/ o pai, por qualquer motivo
    onWeekNavigated: () => void;    // só quando o usuário navega a semana explicitamente (‹ / Hoje / ›)
    onOpenLead: (negociacaoId: number) => void;
    onChanged: () => void;         // refresh do RotasView (leads/tarefas do mês)
    totalLeads: number;
    reloadSignal?: number;         // bump pelo pai (realtime/reagendamento) p/ o grid recarregar junto com o mês
    renderListaCompleta: () => React.ReactNode; // "Ver todos os leads" (lista plana existente)
}

const isoOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const mondayOf = (d: Date) => { const x = new Date(d); const dow = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dow); x.setHours(12, 0, 0, 0); return x; };
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const DIAS_LABEL = ['SEG', 'TER', 'QUA', 'QUI', 'SEX'];

const prioLabel: Record<ClusterPrioridade, string> = { alta: 'PRIORIDADE ALTA', media: 'MÉDIA', baixa: 'BAIXA' };
const prioClass: Record<ClusterPrioridade, string> = {
    alta: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    media: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    baixa: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};

// Escala de PLANEJAMENTO orientada à meta (adendo §4): planejar 15 é o ideal;
// 12-14 bate a meta sem margem; menos de 12 não fecha o dia.
const capClass = (n: number) =>
    n >= META_DIA ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
        : n >= META_CONCLUIDAS_DIA ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
            : n === 0 ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';

// Escala de EXECUÇÃO p/ dias já em andamento/passados (spec §4C):
// verde = tudo concluído · amber = parcial · vermelho = passou sem concluir.
const execClass = (concl: number, total: number, past: boolean) =>
    total > 0 && concl >= total ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
        : concl > 0 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
            : past ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';

const fmtDiaBR = (iso: string) => new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

const AgendaSemana: React.FC<AgendaSemanaProps> = ({ leads, vendedores, vendedorId, profileId, dataSel, onSelectDate, onWeekChange, onWeekNavigated, onOpenLead, onChanged, totalLeads, reloadSignal, renderListaCompleta }) => {
    const { addToast } = useToast();
    const [verTodos, setVerTodos] = useState(false);
    // Sugestões de agrupamento (clusters): colapsadas por padrão (spec §4B) — a semana é a área principal.
    const [showSugestoes, setShowSugestoes] = useState(false);
    const [weekStart, setWeekStart] = useState(() => mondayOf(new Date(dataSel + 'T12:00:00')));
    const [visitas, setVisitas] = useState<WeekVisita[]>([]);
    const [busy, setBusy] = useState(false);
    // Aviso de capacidade (spec §5): promessa resolvida pela escolha do usuário.
    const [capAviso, setCapAviso] = useState<{ iso: string; add: number; proximo: string | null; resolve: (v: string | null) => void } | null>(null);

    const dias = useMemo(() => Array.from({ length: 5 }, (_, i) => addDays(weekStart, i)), [weekStart]);
    const diasIso = useMemo(() => dias.map(isoOf), [dias]);

    const clusters = useMemo(() => clusterLeads(leads), [leads]);
    const totalClusterLeads = useMemo(() => clusters.reduce((s, c) => s + c.leads.length, 0), [clusters]);

    const responsaveis = vendedores.length ? vendedores : (vendedorId ? [vendedorId] : []);

    const fetchWeek = async () => {
        if (responsaveis.length === 0) { setVisitas([]); return; }
        // População do grid por DOIS critérios (ajuste jul/2026 — posicionar pela execução):
        //  · pendentes ficam no dia PLANEJADO (vencimento);
        //  · concluídas ficam no dia em que foram REALIZADAS (data_conclusao), mesmo que
        //    o vencimento seja outro — ex.: visita reagendada p/ frente mas feita adiantada.
        // Por isso buscamos: vencimento na semana OU concluída dentro da semana (por data_conclusao).
        // Inclui tarefa criada sem "Tipo da Visita" (tipo NULL, ex.: direto no Kanban).
        // Visita remota fica fora da rota → só no painel do dia e no badge 📞 do mês.
        const inicioSemana = new Date(weekStart); inicioSemana.setHours(0, 0, 0, 0); // Seg 00:00 local
        const fimSemana = addDays(inicioSemana, 5);                                  // Sáb 00:00 local (cobre Seg–Sex)
        const { data, error } = await supabase
            .from('tarefas')
            .select('id, titulo, vencimento, modalidade, tipo, status, data_conclusao, negociacao_id, negociacoes(titulo, cidade, consumo_medio_kwh)')
            .in('responsavel_id', responsaveis)
            .neq('status', 'cancelado')
            .or(`and(vencimento.gte.${diasIso[0]},vencimento.lte.${diasIso[4]}),and(status.eq.concluido,data_conclusao.gte.${inicioSemana.toISOString()},data_conclusao.lt.${fimSemana.toISOString()})`);
        if (error) { console.error(error); return; }
        const presenciais = ((data || []) as unknown as WeekVisita[]).filter(v => isPresencial(v.modalidade));
        setVisitas(presenciais);
    };

    useEffect(() => { fetchWeek(); /* eslint-disable-next-line */ }, [weekStart, vendedores.join(','), vendedorId, reloadSignal]);

    // Mantém a semana visível contendo o dia selecionado no painel (coerência Mês ↔ Semana):
    // clicar um dia no calendário Mês e voltar à Semana mostra a semana daquele dia.
    useEffect(() => {
        const m = mondayOf(new Date(dataSel + 'T12:00:00'));
        setWeekStart(prev => isoOf(prev) === isoOf(m) ? prev : m);
    }, [dataSel]);

    // Espelha a semana exibida p/ o pai (barra de indicadores) — qualquer origem da mudança.
    useEffect(() => { onWeekChange(isoOf(weekStart)); }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

    // Dia em que a data_conclusao (timestamptz) cai no fuso local — mesma régua de `isoOf`.
    const isoLocalDaConclusao = (ts: string) => { const d = new Date(ts); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
    // Coluna da visita no grid: concluída → dia em que foi REALIZADA; pendente → dia PLANEJADO.
    const diaDaVisita = (v: WeekVisita) =>
        v.status === 'concluido' && v.data_conclusao ? isoLocalDaConclusao(v.data_conclusao) : (v.vencimento || '').slice(0, 10);

    const visitasDoDia = (iso: string) => visitas.filter(v => diaDaVisita(v) === iso);

    // Próximo dia útil (Seg–Sáb) com capacidade nos próximos 14 dias, p/ o aviso de "dia lotado".
    const proximoDiaLivre = async (aPartirIso: string): Promise<string | null> => {
        const start = new Date(aPartirIso + 'T12:00:00');
        const candidatos: string[] = [];
        for (let i = 1; i <= 14 && candidatos.length < 12; i++) {
            const d = addDays(start, i);
            if (d.getDay() === 0) continue; // pula domingo
            candidatos.push(isoOf(d));
        }
        if (candidatos.length === 0 || responsaveis.length === 0) return null;
        const { data } = await supabase
            .from('tarefas')
            .select('vencimento, modalidade')
            .in('responsavel_id', responsaveis)
            .gte('vencimento', candidatos[0])
            .lte('vencimento', candidatos[candidatos.length - 1])
            .neq('status', 'cancelado');
        const counts: Record<string, number> = {};
        for (const r of (data || []) as { vencimento: string; modalidade?: string | null }[]) {
            if (isRemota(r.modalidade)) continue; // só presenciais ocupam capacidade da rota
            const k = (r.vencimento || '').slice(0, 10);
            counts[k] = (counts[k] || 0) + 1;
        }
        return candidatos.find(iso => (counts[iso] || 0) < META_DIA) || null;
    };

    // Se agendar/mover para `iso` estourar a meta, pede confirmação e devolve o dia a usar (ou null p/ cancelar).
    const guardCapacidade = (iso: string, add: number): Promise<string | null> =>
        new Promise(async resolve => {
            if (visitasDoDia(iso).length + add <= META_DIA) return resolve(iso);
            const proximo = await proximoDiaLivre(iso);
            setCapAviso({ iso, add, proximo, resolve });
        });

    const criarVisitas = async (alvo: MapaLead[], iso: string) => {
        if (alvo.length === 0) return;
        setBusy(true);
        try {
            const agora = new Date().toISOString();
            const rows = alvo.map(l => ({
                titulo: `Visita: ${l.titulo}`,
                responsavel_id: l.responsavel_id || vendedorId,
                solicitante_id: profileId,
                prioridade: 'media',
                vencimento: iso,
                status: 'a_iniciar',
                negociacao_id: l.id,
                tipo: 'visita',
                modalidade: 'presencial',
                created_at: agora,
            }));
            const { error } = await supabase.from('tarefas').insert(rows);
            if (error) throw error;
            const d = new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR');
            addToast(alvo.length === 1 ? `Visita agendada para ${d} 📅` : `${alvo.length} visitas agendadas para ${d} 📅`, 'success');
            logUsageEvent({ userId: profileId, modulo: 'crm', funcionalidade: 'rotas', acao: 'agendar_visitas_selecionadas', origem: 'acao' });
            await fetchWeek();
            onChanged();
        } catch (err) {
            console.error('Erro ao agendar visita(s):', err);
            addToast('Erro ao agendar a(s) visita(s).', 'error');
        } finally {
            setBusy(false);
        }
    };

    const agendar = async (alvo: MapaLead[], iso: string) => {
        const escolhido = await guardCapacidade(iso, alvo.length);
        if (escolhido) await criarVisitas(alvo, escolhido);
    };

    const moverVisita = async (id: number, iso: string) => {
        setBusy(true);
        try {
            const { error } = await supabase.from('tarefas').update({ vencimento: iso, status: 'a_iniciar' }).eq('id', id);
            if (error) throw error;
            logUsageEvent({ userId: profileId, modulo: 'crm', funcionalidade: 'rotas', acao: 'reagendar_visita', origem: 'acao' });
            await fetchWeek();
            onChanged();
        } catch (err) {
            console.error('Erro ao mover visita:', err);
            addToast('Erro ao mover a visita.', 'error');
        } finally {
            setBusy(false);
        }
    };

    const mover = async (id: number, iso: string) => {
        const escolhido = await guardCapacidade(iso, 1);
        if (escolhido) await moverVisita(id, escolhido);
    };

    const onDragEnd = async (result: DropResult) => {
        const { draggableId, destination } = result;
        if (!destination) return;
        const destIso = destination.droppableId;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(destIso)) return; // só os dias aceitam drop
        if (draggableId.startsWith('lead:')) {
            const leadId = Number(draggableId.slice(5));
            const lead = leads.find(l => l.id === leadId);
            if (lead) {
                if (visitas.some(v => v.negociacao_id === leadId && v.vencimento.slice(0, 10) === destIso)) {
                    addToast('Esse lead já tem visita nesse dia.', 'info');
                    return;
                }
                await agendar([lead], destIso);
            }
        } else if (draggableId.startsWith('visita:')) {
            const id = Number(draggableId.slice(7));
            const v = visitas.find(x => x.id === id);
            if (v && v.vencimento.slice(0, 10) !== destIso) await mover(id, destIso);
        }
    };

    // Agendar cluster inteiro num dia (select por card)
    const [clusterDia, setClusterDia] = useState<Record<string, string>>({});
    const diaDoCluster = (id: string) => clusterDia[id] || diasIso[0];

    return (
        <div className="space-y-4">
            {/* Banner */}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-100 dark:border-emerald-900/40 bg-gradient-to-r from-emerald-50 to-white dark:from-emerald-900/20 dark:to-gray-800 border-l-4 border-l-emerald-500 px-4 py-3">
                <p className="text-sm text-gray-700 dark:text-gray-200 flex items-center gap-2">
                    <Lightbulb size={16} className="text-emerald-500 shrink-0" />
                    <span>Planeje a semana: clique num dia para vê-lo no painel e gerar a <b className="text-emerald-700 dark:text-emerald-400">Rota Inteligente</b>. Precisa de ideias? Abra as <b className="text-emerald-700 dark:text-emerald-400">Sugestões</b> ({clusters.length}) e arraste um lead para um dia.</span>
                </p>
                <button
                    onClick={() => setVerTodos(v => !v)}
                    className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-200 hover:border-primary/40 hover:text-primary transition-colors shrink-0"
                >
                    <ListFilter size={13} /> {verTodos ? 'Ver sugestões' : `Ver todos os leads (${totalLeads})`}
                </button>
            </div>

            {verTodos ? (
                renderListaCompleta()
            ) : (
                <DragDropContext onDragEnd={onDragEnd}>
                    <div className={showSugestoes ? 'grid grid-cols-1 xl:grid-cols-[minmax(0,360px)_1fr] gap-4 items-start' : ''}>
                        {/* Sugestões de agrupamento (colapsáveis por padrão — spec §4B) */}
                        {showSugestoes && (
                        <div>
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">Sugestões de agrupamento</h3>
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">{clusters.length} clusters</span>
                                </div>
                                <button onClick={() => setShowSugestoes(false)} title="Fechar sugestões" className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={14} /></button>
                            </div>
                            {clusters.length === 0 ? (
                                <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 p-8 text-center text-sm text-gray-400">
                                    Nenhum lead no funil para agrupar.
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[640px] overflow-y-auto pr-1">
                                    {clusters.slice(0, 12).map(c => (
                                        <div key={c.id} className="relative bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 hover:shadow-md transition-shadow">
                                            <span className={`absolute top-3 right-3 text-[9px] font-extrabold px-2 py-0.5 rounded-full ${prioClass[c.prioridade]}`}>{prioLabel[c.prioridade]}</span>
                                            <p className="text-sm font-bold text-gray-900 dark:text-white pr-24">{c.cidade}</p>
                                            <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-md mt-1 mb-2.5 ${familiaTag(c.familia)}`}>{c.familia}</span>
                                            <div className="flex items-center gap-4 text-[11px] text-gray-500 dark:text-gray-400 mb-2.5">
                                                <span><b className="text-gray-800 dark:text-gray-100">{c.leads.length}</b> leads</span>
                                                <span className="flex items-center gap-0.5"><Zap size={11} className="text-amber-500" /> <b className="text-gray-800 dark:text-gray-100">{Math.round(c.kwhTotal).toLocaleString('pt-BR')}</b> kWh</span>
                                                <span><b className="text-gray-800 dark:text-gray-100">{c.diasParado}</b>d parado</span>
                                            </div>
                                            <Droppable droppableId={`cluster:${c.id}`} isDropDisabled>
                                                {(prov) => (
                                                    <div ref={prov.innerRef} {...prov.droppableProps} className="border-t border-dashed border-gray-100 dark:border-gray-700 pt-2 mb-2.5 space-y-0.5">
                                                        {c.leads.slice(0, 4).map((l, idx) => (
                                                            <Draggable key={l.id} draggableId={`lead:${l.id}`} index={idx}>
                                                                {(dp, snap) => (
                                                                    <div
                                                                        ref={dp.innerRef} {...dp.draggableProps} {...dp.dragHandleProps}
                                                                        onClick={() => onOpenLead(l.id)}
                                                                        title="Arraste para um dia da semana · clique para abrir"
                                                                        className={`flex items-center justify-between gap-2 text-[11px] rounded-lg px-1.5 py-1 cursor-grab active:cursor-grabbing ${snap.isDragging ? 'bg-primary/10 shadow-lg' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                                                                    >
                                                                        <span className="truncate text-gray-600 dark:text-gray-300">{l.titulo}</span>
                                                                        <span className="text-gray-400 shrink-0">{Math.round(l.consumo_medio_kwh || 0).toLocaleString('pt-BR')} kWh</span>
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        ))}
                                                        {prov.placeholder}
                                                        {c.leads.length > 4 && <p className="text-[10px] text-gray-400 px-1.5 pt-0.5">+ {c.leads.length - 4} leads</p>}
                                                    </div>
                                                )}
                                            </Droppable>
                                            <div className="flex items-center gap-1.5">
                                                <select
                                                    value={diaDoCluster(c.id)}
                                                    onChange={e => setClusterDia(prev => ({ ...prev, [c.id]: e.target.value }))}
                                                    className="text-[11px] rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 px-2 py-1.5 focus:outline-none"
                                                >
                                                    {dias.map((d, i) => (
                                                        <option key={diasIso[i]} value={diasIso[i]}>{DIAS_LABEL[i]} · {d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</option>
                                                    ))}
                                                </select>
                                                <button
                                                    onClick={() => agendar(c.leads, diaDoCluster(c.id))}
                                                    disabled={busy}
                                                    className="flex-1 flex items-center justify-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold rounded-lg bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                                                >
                                                    <CalendarDays size={12} /> Agendar cluster
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        )}

                        {/* Semana de planejamento (área principal — spec §4C) */}
                        <div>
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">Semana de planejamento</h3>
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">{visitas.length} visitas</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    {!showSugestoes && (
                                        <button
                                            onClick={() => setShowSugestoes(true)}
                                            title="Abrir sugestões de agrupamento"
                                            className="flex items-center gap-1.5 px-2.5 py-1.5 mr-1 text-[11px] font-bold rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
                                        >
                                            <Layers size={13} /> Sugestões · {clusters.length}
                                        </button>
                                    )}
                                    <button onClick={() => { setWeekStart(addDays(weekStart, -7)); onWeekNavigated(); }} className="p-1.5 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10"><ChevronLeft size={15} /></button>
                                    <button onClick={() => { setWeekStart(mondayOf(new Date())); onWeekNavigated(); }} className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 hover:text-primary px-1.5">Hoje</button>
                                    <button onClick={() => { setWeekStart(addDays(weekStart, 7)); onWeekNavigated(); }} className="p-1.5 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10"><ChevronRight size={15} /></button>
                                </div>
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-3">
                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                    {dias.map((d, i) => {
                                        const iso = diasIso[i];
                                        const doDia = visitasDoDia(iso);
                                        const concl = doDia.filter(v => v.status === 'concluido').length;
                                        const hoje = iso === isoOf(new Date());
                                        const past = iso < isoOf(new Date());
                                        // Dia passado OU já com alguma conclusão → escala de execução; senão, escala de capacidade.
                                        const usarExec = past || concl > 0;
                                        const selecionado = iso === dataSel;
                                        return (
                                            <Droppable droppableId={iso} key={iso}>
                                                {(prov, snap) => (
                                                    <div
                                                        ref={prov.innerRef} {...prov.droppableProps}
                                                        className={`rounded-xl border p-2 min-h-[210px] transition-colors ${selecionado ? 'ring-2 ring-primary/60 border-primary' : hoje ? 'border-emerald-300 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-900/10' : 'border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-700/30'} ${snap.isDraggingOver ? 'ring-2 ring-primary/40 bg-primary/5' : ''}`}
                                                    >
                                                        <button
                                                            onClick={() => onSelectDate(iso)}
                                                            title="Selecionar este dia no painel"
                                                            className="w-full flex items-center justify-between mb-2 px-0.5"
                                                        >
                                                            <span className={`text-[10.5px] font-bold ${selecionado ? 'text-primary' : 'text-gray-600 dark:text-gray-300'}`}>{DIAS_LABEL[i]} {d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</span>
                                                            <span
                                                                title={usarExec ? `${doDia.length} visita(s) · ${concl} concluída(s)` : `${doDia.length} de ${META_DIA} visitas planejadas`}
                                                                className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded-full ${usarExec ? execClass(concl, doDia.length, past) : capClass(doDia.length)}`}
                                                            >
                                                                {usarExec ? `${concl}/${doDia.length} ✓` : `${doDia.length}/${META_DIA}`}
                                                            </span>
                                                        </button>
                                                        {doDia.map((v, idx) => {
                                                            const feita = v.status === 'concluido';
                                                            // Visita de pós-venda usa o laranja da marca: o vendedor precisa
                                                            // distinguir de bate na grade sem abrir o card (spec §6).
                                                            const posVenda = v.tipo === 'pos_venda';
                                                            return (
                                                            // Concluída aparece no dia em que foi realizada → arrastar (que reabre a visita) não faz sentido.
                                                            <Draggable key={v.id} draggableId={`visita:${v.id}`} index={idx} isDragDisabled={feita}>
                                                                {(dp, dsnap) => (
                                                                    <div
                                                                        ref={dp.innerRef} {...dp.draggableProps} {...dp.dragHandleProps}
                                                                        onClick={() => v.negociacao_id && onOpenLead(v.negociacao_id)}
                                                                        title={feita ? 'Visita concluída neste dia' : posVenda ? 'Visita de pós-venda' : undefined}
                                                                        className={`rounded-lg border bg-white dark:bg-gray-800 px-2 py-1.5 mb-1.5 ${posVenda ? 'border-l-[3px] border-l-secondary' : ''} ${feita ? 'cursor-pointer opacity-60' : 'cursor-grab active:cursor-grabbing'} ${dsnap.isDragging ? 'shadow-lg border-primary/40' : 'border-gray-100 dark:border-gray-700'}`}
                                                                    >
                                                                        {posVenda && (
                                                                            <span className="inline-flex items-center gap-1 mb-0.5 px-1.5 py-0.5 rounded-full bg-secondary/15 text-secondary text-[8.5px] font-extrabold uppercase tracking-wider">
                                                                                <HeartHandshake size={8} /> Pós-venda
                                                                            </span>
                                                                        )}
                                                                        <p className="text-[11px] font-bold text-gray-800 dark:text-gray-100 truncate">{v.negociacoes?.titulo || v.titulo}</p>
                                                                        <p className="text-[10px] text-gray-400 truncate flex items-center gap-1">
                                                                            <MapPin size={9} /> {v.negociacoes?.cidade || '—'}{(v.negociacoes?.consumo_medio_kwh || 0) > 0 ? ` · ${Math.round(v.negociacoes!.consumo_medio_kwh!).toLocaleString('pt-BR')} kWh` : ''}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                            );
                                                        })}
                                                        {prov.placeholder}
                                                        {doDia.length === 0 && (
                                                            <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-600 text-[10px] text-gray-400 text-center py-3 px-1">
                                                                arraste um lead aqui
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </Droppable>
                                        );
                                    })}
                                </div>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-[10.5px] text-gray-500 dark:text-gray-400">
                                    <span className="font-semibold text-gray-400">Planejamento (meta {META_CONCLUIDAS_DIA} concluídas/dia):</span>
                                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> ≥{META_DIA} ideal (margem)</span>
                                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" /> {META_CONCLUIDAS_DIA}–{META_DIA - 1} sem margem</span>
                                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /> &lt;{META_CONCLUIDAS_DIA} abaixo da meta</span>
                                    <span className="font-semibold text-gray-400 ml-1">· Execução (dias passados): <b className="text-emerald-500">tudo feito</b> / <b className="text-amber-500">parcial</b> / <b className="text-red-500">atrasado</b> ✓</span>
                                </div>
                            </div>
                            <p className="text-[11px] text-gray-400 flex items-center gap-1.5 mt-3"><Sparkles size={12} /> Clique num dia para vê-lo no painel e gerar a <b>Rota Inteligente</b>. Arraste visitas entre dias sem desmontar rota.</p>
                        </div>
                    </div>
                </DragDropContext>
            )}

            {/* Aviso de capacidade excedida (spec §5) */}
            {capAviso && (
                <div className="fixed inset-0 z-[2100] flex items-center justify-center p-4 bg-black/50" onClick={() => { capAviso.resolve(null); setCapAviso(null); }}>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Dia lotado
                            </h2>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                                {fmtDiaBR(capAviso.iso)} já tem {visitasDoDia(capAviso.iso).length}/{META_DIA} visitas.
                                {capAviso.proximo ? <> Próximo dia livre: <b>{fmtDiaBR(capAviso.proximo)}</b>.</> : ' Não há dia livre nos próximos 14 dias.'}
                            </p>
                        </div>
                        <div className="p-5 space-y-2">
                            {capAviso.proximo && (
                                <button
                                    onClick={() => { const p = capAviso.proximo!; capAviso.resolve(p); setCapAviso(null); }}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl bg-primary text-white hover:opacity-90 transition-opacity"
                                >
                                    <CalendarDays size={15} /> Usar {fmtDiaBR(capAviso.proximo)}
                                </button>
                            )}
                            <button
                                onClick={() => { const iso = capAviso.iso; capAviso.resolve(iso); setCapAviso(null); }}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            >
                                Agendar em {fmtDiaBR(capAviso.iso)} mesmo assim
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgendaSemana;
